/**
 * Отказ версионирования для показа пользователю.
 *
 * Норма: spec/backend/business-model/versioning.md — отказы версионирования
 * доходят до пользователя: он видит причину, а не обобщённое «не удалось»
 * (api-errors-R08). Текст причины собирается по признаку причины и параметрам
 * из словаря интерфейса (api-errors-R04).
 *
 * Обобщённый текст операции («Не удалось создать ветку») — только когда текста
 * по признаку причины нет. Серверное сообщение пользователю не показывается:
 * отступление R04 версионирование у себя не называет (BUG-0071).
 *
 * Одна причина у разных операций называется по-разному: «нет права» на
 * создание ветки и на коммит — разные тексты (BUG-0073). Поэтому у каждой
 * операции своё пространство текстов отказа, оно ищется раньше общего
 * `errors.<признак>`.
 */

import type { IApiError } from "@/shared/types";
import { UNKNOWN_ERROR_CODE } from "@/shared/api";

/**
 * Пространства текстов отказа по операциям версионирования для
 * `useApiErrorMessage`: `<пространство>.errors.<признак>`, затем
 * `errors.<признак>` (api-errors-R04, R08).
 */
export const VERSIONING_FAILURE_NAMESPACES = {
  checkout: ["versioning.checkoutFailure"],
  commit: ["versioning.commitFailure"],
  history: ["versioning.historyFailure"],
} as const satisfies Record<string, readonly string[]>;

/**
 * Отказ в форме для `showApiError`: признак и параметры причины — продукта,
 * запасное сообщение — обобщённый текст операции.
 *
 * Отказ без признака причины (`UNKNOWN_ERROR` — продукт ничего не прислал или
 * ошибка не от запроса) причины не называет, и показывается обобщённый текст
 * операции, а не общий «неизвестная ошибка».
 */
export function versioningFailureForDisplay(
  error: IApiError | null,
  operationFailedText: string
): IApiError {
  const code = error === null || error.code === UNKNOWN_ERROR_CODE ? "" : error.code;
  return {
    code,
    message: operationFailedText,
    ...(error?.params !== undefined ? { params: error.params } : {}),
  };
}
